import axios from 'axios';
import { createQuery } from 'react-query-kit';

import { LOCAL_STORAGE } from '@/constants';
import { restUrl } from '@/services/utils';
import { AdditionalCategory, DataModelAttributeOption, PersistedItem } from '@/types/DataModel';

import { normalizeOptionListOptions } from './optionListUtils';

axios.interceptors.request.use((config) => {
    const storedLanguage = localStorage.getItem(LOCAL_STORAGE.LANGUAGE_KEY) || 'en';
    // The stored `languageKey` uses Java-style `en_GB` (underscore) but
    // `Accept-Language` must be a BCP-47 tag (RFC 7231 § 5.3.5). The
    // underscore is also not a CORS-safelisted request-header value byte,
    // so leaving it in the header forces cross-origin preflights (e.g.
    // the GCS signed-URL PUT used by the bulk-subscription and large
    // asset-folder uploads) to include `Accept-Language` in
    // `Access-Control-Request-Headers`, which Google Cloud Storage
    // rejects. Normalising to hyphens keeps the header both spec-correct
    // and CORS-safelisted. The Java backend (`LocaleUtil.toLocale`)
    // already accepts either delimiter, so this is transparent server-side.
    config.headers['Accept-Language'] = storedLanguage.replace(/_/g, '-');
    return config;
});

interface KeyValue {
    key: string;
    value: string;
}

interface DynamicValuesVariables {
    url: string;
}

const useDynamicEnumValues = createQuery<KeyValue[], DynamicValuesVariables, Error>({
    queryKey: ['dynamicEnumValues'], // Query key will be additionally the codelist name
    fetcher: async ({ url }) => {
        const response = await axios.get(url);

        return response.data ?? [];
    }
});

interface CodelistPairsVariables {
    codelistName: string;
}

const useCodelistPairs = createQuery<KeyValue[], CodelistPairsVariables, Error>({
    queryKey: ['codelists/pairs'], // Query key will be additionally the codelist name
    refetchOnMount: false,
    fetcher: async ({ codelistName }) => {
        const response = await axios.get(restUrl(`codelists/${codelistName}/pairs?limit=0`));

        return response.data ?? [];
    }
});

interface OptionListsVariables {
    optionListName: string;
    serviceName: string;
}

const fetchOptionListOptions = async (
    optionListName: string,
    serviceName: string
): Promise<DataModelAttributeOption[]> => {
    const response = await axios.get(
        restUrl(`datamodel/optionLists/${optionListName}/options?serviceName=${serviceName}`)
    );
    // Normalize the server's `{ name, label, … }` shape to the
    // `{ key, value, … }` shape that DataModelAttributeOption (and the rest of
    // the React code) expects. See optionListUtils.ts for context.
    return normalizeOptionListOptions(response.data ?? []);
};

const useOptionLists = createQuery<DataModelAttributeOption[], OptionListsVariables, Error>({
    queryKey: ['optionLists'], // Query key will be additionally the codelist name
    fetcher: async ({ optionListName, serviceName }) => {
        return fetchOptionListOptions(optionListName, serviceName);
    }
});

interface ReferencedItemsVariables {
    keyword: string;
    referenceableCategories: string[];
}

const useReferencedItems = createQuery<PersistedItem[], ReferencedItemsVariables, Error>({
    queryKey: ['referencedItems'],
    fetcher: async ({ keyword, referenceableCategories }) => {

        let query = `${keyword}`;

        referenceableCategories.forEach((category, index) => {
            const escapedCategoryName = category.replace(/:\s*/g, '\\:');

            if (index === 0) {
                query += ' AND (';
            } else {
                query += ' OR ';
            }

            query += `category__:${escapedCategoryName}`;
        });

        query += ')';

        const response = await axios.get(restUrl(`items?keyword=${encodeURIComponent(query)}`));
        return response.data ?? [];
    }
});

interface AdditionalCategoryVariables {
    dataModel: string;
    extension: string;
    leaf?: boolean;
    limit?: number;
    parent?: string;
    query?: string;
}

// FIXME: replace with new endpoints:
// - GET datamodel/categories/{nameOrCode}?serviceName=...&dataModel=...
// - GET datamodel/categories/{nameOrCode}/children?serviceName=...&dataModel=...
// - GET datamodel/categories/{nameOrCode}/ancestors?serviceName=...&dataModel=...
// - GET datamodel/categories?serviceName=...&dataModel=...&q=...
// - POST datamodel/categories?serviceName=...&dataModel=... BODY [String]
// - POST datamodel/categories/children?serviceName=...&dataModel=... BODY [String]
const useAdditionalCategories = createQuery<AdditionalCategory[], AdditionalCategoryVariables, Error>({
    queryKey: ['AdditionalCategories'], // Query key will additionally be the passed variables
    fetcher: async ({ parent, dataModel, extension, leaf= false, query, limit = 50 }) => {
        const q = query? `&q=${query}` : '';
        if (leaf && query) {
            const response = await axios.get(restUrl(`datamodel/retrieval/categories?dataModel=${dataModel}&extension=${extension}&limit=${limit}&leaf=true${q}`));

            return response.data ?? [];
        }
        if (parent) {
            const response = await axios.get(restUrl(`datamodel/retrieval/categories?dataModel=${dataModel}&extension=${extension}&limit=${limit}&parent=${parent}`));

            return response.data ?? [];
        }
        return [];
    }
});

// FIXME: replace with new endpoints:
// - GET datamodel/categories/{nameOrCode}?serviceName=...&dataModel=...
// - GET datamodel/categories/{nameOrCode}/children?serviceName=...&dataModel=...
// - GET datamodel/categories/{nameOrCode}/ancestors?serviceName=...&dataModel=...
// - GET datamodel/categories?serviceName=...&dataModel=...&q=...
// - POST datamodel/categories?serviceName=...&dataModel=... BODY [String]
// - POST datamodel/categories/children?serviceName=...&dataModel=... BODY [String]
const useAdditionalCategory = createQuery<AdditionalCategory, AdditionalCategoryVariables, Error>({
    queryKey: ['AdditionalCategory'], // Query key will additionally be the passed variables
    fetcher: async ({ dataModel, extension, query }) => {
        // Exact lookup. The legacy retrieval search with limit=1 returns the
        // first keyword hit, which is often a different category than the code
        // stored on the item.
        const params = new URLSearchParams({ dataModel });
        if (extension) {
            params.set('serviceName', extension);
        }
        const response = await axios.get<AdditionalCategory>(
            restUrl(`datamodel/categories/${encodeURIComponent(query ?? '')}?${params.toString()}`));

        return response.data;
    }
});

export {
    fetchOptionListOptions,
    useAdditionalCategories,
    useAdditionalCategory,
    useCodelistPairs,
    useDynamicEnumValues,
    useOptionLists,
    useReferencedItems
};
